import { createFileRoute, redirect } from '@tanstack/react-router';
import { settingsQueryOptions } from '@/api/services/settings';
import { assertAuth0ExtParamsLength, type CustomAuthorizationParams } from '@/helpers/auth';
import { AUTH_REDIRECT_URL, BASE_URL } from '@/helpers/url';
import { head } from '@/router/Head';
import { UnavailableAuth0Context } from '../__root.hooks';

export const Route = createFileRoute('/(auth)/signup')({
  beforeLoad: async ({ context: { auth, queryClient } }) => {
    if (auth === UnavailableAuth0Context) {
      // Discriminate type to satisfy the compiler: this case should never happen because the route is only rendered when Auth0 context is available
      throw new Error('Auth0 context is not available');
    }

    if (auth.isAuthenticated) {
      throw redirect({ href: AUTH_REDIRECT_URL, replace: true });
    }

    const authParams: CustomAuthorizationParams = {
      'ext-back_link': BASE_URL.toString(),
      'ext-cdn': window.__QDRANT_CLOUD__.qdrant_cdn_url.replace(/\/+$/, ''),
      'ext-home-url': BASE_URL.origin,
      'ext-onetrust-domain-id': window.__QDRANT_CLOUD__.onetrust_domain_id,
      'ext-segment-write-key': window.__QDRANT_CLOUD__.segment.write_key,
    };

    // The `incident_mode` custom parameter is set when INCIDENT_MODE is active via /settings.json
    const { incident } = await queryClient.fetchQuery(settingsQueryOptions);
    if (incident) {
      authParams['ext-incident_mode'] = 'true';
    }

    assertAuth0ExtParamsLength(authParams);

    await auth.loginWithRedirect({
      authorizationParams: {
        audience: window.__QDRANT_CLOUD__.auth0.audience,
        scope: window.__QDRANT_CLOUD__.auth0.scope,
        redirect_uri: AUTH_REDIRECT_URL,
        // One page logs in and signs up alike: Auth0 creates the user on the first login.
        screen_hint: 'login',
        ...authParams,
      },
    });
  },
  staticData: {
    title: 'Signing up ...',
    domain: 'auth',
  },
  head,
});
